import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, 
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell 
} from 'recharts';
import './AdminDashboardPage.css';

const COLORS = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const AdminDashboardPage = () => {
    const { user } = useAuth();
    
    // UI tabs: 'analytics', 'users', 'jobs', 'maintenance'
    const [activeTab, setActiveTab] = useState('analytics');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    
    // States
    const [analytics, setAnalytics] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(true);
    
    // Users state
    const [usersPage, setUsersPage] = useState({ content: [], number: 0, totalPages: 1 });
    const [usersSearch, setUsersSearch] = useState('');
    const [usersRole, setUsersRole] = useState('');
    const [usersCurrentPage, setUsersCurrentPage] = useState(0);
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    // Jobs state
    const [jobsPage, setJobsPage] = useState({ content: [], number: 0, totalPages: 1 });
    const [jobsSearch, setJobsSearch] = useState('');
    const [jobsCurrentPage, setJobsCurrentPage] = useState(0);
    const [loadingJobs, setLoadingJobs] = useState(false);
    
    // Applications state
    const [applicationsPage, setApplicationsPage] = useState({ content: [], number: 0, totalPages: 1 });
    const [applicationsSearch, setApplicationsSearch] = useState('');
    const [applicationsCurrentPage, setApplicationsCurrentPage] = useState(0);
    const [loadingApplications, setLoadingApplications] = useState(false);
    
    // Maintenance state
    const [cleanupDays, setCleanupDays] = useState(180);
    const [cleaning, setCleaning] = useState(false);
    
    // Toast alerts
    const [alert, setAlert] = useState(null);

    const showAlert = (message, type = 'success') => {
        setAlert({ message, type });
        setTimeout(() => setAlert(null), 5000);
    };

    // Fetch Analytics Data
    const fetchAnalytics = async () => {
        setLoadingAnalytics(true);
        try {
            const res = await api.get('admin/analytics');
            if (res.data?.success) {
                setAnalytics(res.data.data);
            } else {
                showAlert('Failed to load analytics: ' + res.data?.message, 'danger');
            }
        } catch (err) {
            console.error('Error fetching analytics:', err);
            showAlert('Server error occurred while loading analytics.', 'danger');
        } finally {
            setLoadingAnalytics(false);
        }
    };

    // Fetch Users list
    const fetchUsers = async (page = 0) => {
        setLoadingUsers(true);
        try {
            const roleQuery = usersRole ? `&roleId=${usersRole}` : '';
            const res = await api.get(`admin/users?page=${page}&size=10&search=${usersSearch}${roleQuery}`);
            if (res.data?.success) {
                setUsersPage(res.data.data);
                setUsersCurrentPage(page);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            showAlert('Failed to load users list.', 'danger');
        } finally {
            setLoadingUsers(false);
        }
    };

    // Fetch Jobs list
    const fetchJobs = async (page = 0) => {
        setLoadingJobs(true);
        try {
            const res = await api.get(`admin/jobs?page=${page}&size=10&search=${jobsSearch}`);
            if (res.data?.success) {
                setJobsPage(res.data.data);
                setJobsCurrentPage(page);
            }
        } catch (err) {
            console.error('Error fetching jobs:', err);
            showAlert('Failed to load jobs list.', 'danger');
        } finally {
            setLoadingJobs(false);
        }
    };

    // Fetch Applications list
    const fetchApplications = async (page = 0) => {
        setLoadingApplications(true);
        try {
            const res = await api.get(`admin/applications?page=${page}&size=10&search=${applicationsSearch}`);
            if (res.data?.success) {
                setApplicationsPage(res.data.data);
                setApplicationsCurrentPage(page);
            }
        } catch (err) {
            console.error('Error fetching applications:', err);
            showAlert('Failed to load applications list.', 'danger');
        } finally {
            setLoadingApplications(false);
        }
    };

    // Handle Delete Application
    const handleDeleteApplication = async (appId) => {
        if (!window.confirm('Are you sure you want to permanently delete this application?')) return;
        try {
            const res = await api.delete(`admin/applications/${appId}`);
            if (res.data?.success) {
                showAlert('Application deleted successfully.', 'success');
                fetchApplications(applicationsCurrentPage);
            } else {
                showAlert(res.data?.error || 'Failed to delete application.', 'danger');
            }
        } catch (err) {
            console.error('Error deleting application:', err);
            showAlert('Error deleting application.', 'danger');
        }
    };

    // Initial load
    useEffect(() => {
        fetchAnalytics();
    }, []);

    // Load data when tabs or filters change
    useEffect(() => {
        if (activeTab === 'users') {
            fetchUsers(0);
        } else if (activeTab === 'jobs') {
            fetchJobs(0);
        } else if (activeTab === 'applications') {
            fetchApplications(0);
        } else if (activeTab === 'analytics') {
            fetchAnalytics();
        }
    }, [activeTab, usersRole]);

    // Handle User Status toggle
    const handleToggleUserStatus = async (userId) => {
        try {
            const res = await api.put(`admin/users/${userId}/toggle-status`);
            if (res.data?.success) {
                showAlert('User status updated successfully.');
                fetchUsers(usersCurrentPage);
            }
        } catch (err) {
            showAlert('Failed to update user status.', 'danger');
        }
    };

    // Handle User Delete
    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to permanently delete this user? This will delete all their profiles, applications, and logs!')) return;
        try {
            const res = await api.delete(`admin/users/${userId}`);
            if (res.data?.success) {
                showAlert('User deleted successfully.');
                fetchUsers(usersCurrentPage);
            }
        } catch (err) {
            showAlert('Failed to delete user.', 'danger');
        }
    };

    // Handle Job Status toggle
    const handleToggleJobStatus = async (jobId) => {
        try {
            const res = await api.put(`admin/jobs/${jobId}/toggle-status`);
            if (res.data?.success) {
                showAlert('Job status updated successfully.');
                fetchJobs(jobsCurrentPage);
            }
        } catch (err) {
            showAlert('Failed to update job status.', 'danger');
        }
    };

    // Handle Job Delete
    const handleDeleteJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to permanently delete this job post? This will cascade-delete all candidates applications and saved lists!')) return;
        try {
            const res = await api.delete(`admin/jobs/${jobId}`);
            if (res.data?.success) {
                showAlert('Job post deleted successfully.');
                fetchJobs(jobsCurrentPage);
            }
        } catch (err) {
            showAlert('Failed to delete job.', 'danger');
        }
    };

    // Handle Cleanup
    const handleRunCleanup = async () => {
        if (!window.confirm(`Are you sure you want to trigger database maintenance? This will permanently delete jobs older than ${cleanupDays} days (and associated candidate data), and deactivate jobs older than 60 days.`)) return;
        setCleaning(true);
        try {
            const res = await api.post(`admin/maintenance/cleanup?daysOlder=${cleanupDays}`);
            if (res.data?.success) {
                showAlert(`Maintenance completed. Permanently removed ${res.data.data} obsolete jobs.`);
                fetchAnalytics();
            }
        } catch (err) {
            showAlert('Maintenance cleanup failed.', 'danger');
        } finally {
            setCleaning(false);
        }
    };

    // Render Charts Helper
    const getRegistrationChartData = () => {
        if (!analytics || !analytics.registrationTrends) return [];
        return Object.entries(analytics.registrationTrends).map(([month, count]) => ({
            month,
            Registrations: count
        }));
    };

    const getJobChartData = () => {
        if (!analytics || !analytics.jobPostTrends) return [];
        return Object.entries(analytics.jobPostTrends).map(([month, count]) => ({
            month,
            JobsPosted: count
        }));
    };

    const getFunnelChartData = () => {
        if (!analytics || !analytics.applicationStatusDistribution) return [];
        return Object.entries(analytics.applicationStatusDistribution).map(([status, count]) => ({
            name: status.replace('_', ' '),
            value: count
        }));
    };

    return (
        <div className="admin-dashboard-container">
            {/* Top mobile navbar */}
            <div className="admin-mobile-header d-lg-none">
                <span className="brand-logo"><i className="fa-solid fa-shield-halved text-cyan me-2"></i>Admin Panel</span>
                <button className="btn sidebar-toggle-btn text-white" onClick={() => setSidebarOpen(!sidebarOpen)}>
                    <i className={`fa-solid ${sidebarOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
                </button>
            </div>

            {/* Sidebar */}
            <aside className={`admin-sidebar ${sidebarOpen ? 'show' : ''}`}>
                <div className="sidebar-brand d-none d-lg-flex">
                    <i className="fa-solid fa-shield-halved text-cyan me-2"></i>
                    <span>Admin Central</span>
                </div>
                <div className="sidebar-user-info">
                    <div className="user-avatar">
                        <i className="fa-solid fa-user-shield text-light"></i>
                    </div>
                    <div className="user-details text-center mt-2">
                        <h6 className="mb-0 text-white fw-semibold">{user?.firstName || 'System'} {user?.lastName || 'Admin'}</h6>
                        <small className="text-cyan text-uppercase tracking-wider">Super Administrator</small>
                    </div>
                </div>
                <nav className="sidebar-nav mt-4">
                    <button 
                        className={`nav-item-btn ${activeTab === 'analytics' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('analytics'); setSidebarOpen(false); }}
                    >
                        <i className="fa-solid fa-chart-line me-3"></i>Analytics Hub
                    </button>
                    <button 
                        className={`nav-item-btn ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('users'); setSidebarOpen(false); }}
                    >
                        <i className="fa-solid fa-users me-3"></i>Manage Users
                    </button>
                    <button 
                        className={`nav-item-btn ${activeTab === 'jobs' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('jobs'); setSidebarOpen(false); }}
                    >
                        <i className="fa-solid fa-briefcase me-3"></i>Moderate Jobs
                    </button>
                    <button 
                        className={`nav-item-btn ${activeTab === 'applications' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('applications'); setSidebarOpen(false); }}
                    >
                        <i className="fa-solid fa-file-invoice me-3"></i>Manage Applications
                    </button>
                    <button 
                        className={`nav-item-btn ${activeTab === 'maintenance' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('maintenance'); setSidebarOpen(false); }}
                    >
                        <i className="fa-solid fa-screwdriver-wrench me-3"></i>Maintenance
                    </button>
                </nav>
                <div className="sidebar-footer mt-auto">
                    <a href="/" className="btn btn-outline-cyan w-100 btn-sm">
                        <i className="fa-solid fa-arrow-left me-2"></i>Exit Portal
                    </a>
                </div>
            </aside>

            {/* Main Area */}
            <main className="admin-main">
                {/* Custom Alert */}
                {alert && (
                    <div className={`alert alert-${alert.type} alert-dismissible fade show custom-toast`} role="alert">
                        <i className={`fa-solid ${alert.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} me-2`}></i>
                        {alert.message}
                        <button type="button" className="btn-close" onClick={() => setAlert(null)}></button>
                    </div>
                )}

                {/* TAB 1: ANALYTICS HUB */}
                {activeTab === 'analytics' && (
                    <div className="fade-in">
                        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                            <h2 className="page-heading mb-0"><i className="fa-solid fa-chart-pie me-2 text-cyan"></i>Analytics Hub</h2>
                            <button className="btn btn-cyan text-dark btn-sm rounded-pill px-3 mt-2 mt-sm-0" onClick={fetchAnalytics} disabled={loadingAnalytics}>
                                <i className={`fa-solid fa-arrows-rotate me-2 ${loadingAnalytics ? 'fa-spin' : ''}`}></i>Refresh Stats
                            </button>
                        </div>

                        {loadingAnalytics ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-cyan mb-3" role="status"></div>
                                <p className="text-muted">Analyzing portal database metrics...</p>
                            </div>
                        ) : (
                            <>
                                {/* KPIs Cards */}
                                <div className="row g-3 mb-4">
                                    <div className="col-6 col-md-4 col-xl-2">
                                        <div className="kpi-card bg-glass">
                                            <div className="kpi-icon text-cyan"><i className="fa-solid fa-users"></i></div>
                                            <div className="kpi-value">{analytics?.totalUsers}</div>
                                            <div className="kpi-label">Total Users</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-xl-2">
                                        <div className="kpi-card bg-glass">
                                            <div className="kpi-icon text-primary"><i className="fa-solid fa-user-tie"></i></div>
                                            <div className="kpi-value">{analytics?.totalRecruiters}</div>
                                            <div className="kpi-label">Recruiters</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-xl-2">
                                        <div className="kpi-card bg-glass">
                                            <div className="kpi-icon text-success"><i className="fa-solid fa-user-graduate"></i></div>
                                            <div className="kpi-value">{analytics?.totalJobSeekers}</div>
                                            <div className="kpi-label">Job Seekers</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-xl-2">
                                        <div className="kpi-card bg-glass">
                                            <div className="kpi-icon text-warning"><i className="fa-solid fa-briefcase"></i></div>
                                            <div className="kpi-value">{analytics?.totalJobs}</div>
                                            <div className="kpi-label">Total Jobs</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-xl-2">
                                        <div className="kpi-card bg-glass">
                                            <div className="kpi-icon text-cyan"><i className="fa-solid fa-bullseye"></i></div>
                                            <div className="kpi-value">{analytics?.activeJobs}</div>
                                            <div className="kpi-label">Active Jobs</div>
                                        </div>
                                    </div>
                                    <div className="col-6 col-md-4 col-xl-2">
                                        <div className="kpi-card bg-glass">
                                            <div className="kpi-icon text-danger"><i className="fa-solid fa-file-signature"></i></div>
                                            <div className="kpi-value">{analytics?.totalApplications}</div>
                                            <div className="kpi-label">Applications</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Charts row */}
                                <div className="row g-4 mb-4">
                                    <div className="col-12 col-xl-8">
                                        <div className="chart-card bg-glass">
                                            <h5 className="chart-title"><i className="fa-solid fa-user-plus text-cyan me-2"></i>Monthly User Registrations</h5>
                                            <div className="chart-container">
                                                {getRegistrationChartData().length === 0 ? (
                                                    <div className="no-data">No registration records found.</div>
                                                ) : (
                                                    <ResponsiveContainer width="100%" height={260}>
                                                        <AreaChart data={getRegistrationChartData()} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                                            <defs>
                                                                <linearGradient id="regGrad" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                                                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                            <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                                                            <YAxis stroke="#64748b" fontSize={11} />
                                                            <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', color: '#0f172a' }} />
                                                            <Area type="monotone" dataKey="Registrations" stroke="#6366f1" fillOpacity={1} fill="url(#regGrad)" />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 col-xl-4">
                                        <div className="chart-card bg-glass">
                                            <h5 className="chart-title"><i className="fa-solid fa-chart-column text-cyan me-2"></i>Application Pipeline</h5>
                                            <div className="chart-container d-flex flex-column align-items-center justify-content-center">
                                                {getFunnelChartData().length === 0 ? (
                                                    <div className="no-data">No applications in pipeline.</div>
                                                ) : (
                                                    <div className="w-100 d-flex flex-column flex-md-row flex-xl-column align-items-center justify-content-around">
                                                        <ResponsiveContainer width={180} height={180}>
                                                            <PieChart>
                                                                <Pie
                                                                    data={getFunnelChartData()}
                                                                    cx="50%"
                                                                    cy="50%"
                                                                    innerRadius={60}
                                                                    outerRadius={80}
                                                                    paddingAngle={3}
                                                                    dataKey="value"
                                                                >
                                                                    {getFunnelChartData().map((entry, index) => (
                                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                    ))}
                                                                </Pie>
                                                                <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', color: '#0f172a' }} />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                        <div className="chart-legend-container mt-3 mt-md-0 mt-xl-3">
                                                            {getFunnelChartData().map((item, idx) => (
                                                                <div key={item.name} className="chart-legend-item">
                                                                    <span className="legend-dot" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                                                                    <span className="legend-label text-capitalize">{item.name}</span>
                                                                    <span className="legend-value fw-bold text-white ms-auto">{item.value}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4">
                                    <div className="col-12">
                                        <div className="chart-card bg-glass">
                                            <h5 className="chart-title"><i className="fa-solid fa-briefcase text-cyan me-2"></i>Monthly Job Post Activity</h5>
                                            <div className="chart-container">
                                                {getJobChartData().length === 0 ? (
                                                    <div className="no-data">No job postings recorded.</div>
                                                ) : (
                                                    <ResponsiveContainer width="100%" height={260}>
                                                        <BarChart data={getJobChartData()}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                            <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                                                            <YAxis stroke="#64748b" fontSize={11} />
                                                            <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', color: '#0f172a' }} />
                                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                                            <Bar dataKey="JobsPosted" fill="#6366f1" name="Jobs Posted" radius={[4, 4, 0, 0]} />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* TAB 2: MANAGE USERS */}
                {activeTab === 'users' && (
                    <div className="fade-in">
                        <h2 className="page-heading mb-4"><i className="fa-solid fa-users me-2 text-cyan"></i>User Management</h2>
                        
                        {/* Filters */}
                        <div className="filter-bar bg-glass p-3 rounded mb-4">
                            <div className="row g-3">
                                <div className="col-12 col-md-6 col-lg-5">
                                    <div className="input-group search-group">
                                        <span className="input-group-text"><i className="fa-solid fa-magnifying-glass"></i></span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Search by name, email..."
                                            value={usersSearch}
                                            onChange={(e) => setUsersSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && fetchUsers(0)}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 col-lg-3">
                                    <select 
                                        className="form-select"
                                        value={usersRole}
                                        onChange={(e) => setUsersRole(e.target.value)}
                                    >
                                        <option value="">All Roles</option>
                                        <option value="1">Recruiter</option>
                                        <option value="2">Job Seeker</option>
                                        <option value="3">Admin</option>
                                    </select>
                                </div>
                                <div className="col-12 col-sm-6 col-md-3 col-lg-4 d-flex gap-2">
                                    <button className="btn btn-cyan text-dark w-100" onClick={() => fetchUsers(0)}>
                                        Search
                                    </button>
                                    <button className="btn btn-outline-cyan w-100" onClick={() => { setUsersSearch(''); setUsersRole(''); fetchUsers(0); }}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="table-responsive bg-glass rounded border border-secondary border-opacity-20 shadow-sm">
                            <table className="table custom-table mb-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Registration Date</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingUsers ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <div className="spinner-border text-cyan" role="status"></div>
                                            </td>
                                        </tr>
                                    ) : usersPage.content.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4 text-muted">No users found matching search query.</td>
                                        </tr>
                                    ) : (
                                        usersPage.content.map((usr) => (
                                            <tr key={usr.userId}>
                                                <td>#{usr.userId}</td>
                                                <td>
                                                    <span className="fw-semibold text-white">{usr.firstName} {usr.lastName}</span>
                                                </td>
                                                <td>{usr.email}</td>
                                                <td>
                                                    <span className={`badge-role ${
                                                        usr.userTypeId?.userTypeName === 'Admin' ? 'bg-danger-sub' :
                                                        usr.userTypeId?.userTypeName === 'Recruiter' ? 'bg-primary-sub' : 'bg-success-sub'
                                                    }`}>
                                                        {usr.userTypeId?.userTypeName}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge-status ${usr.active ? 'bg-active' : 'bg-inactive'}`}>
                                                        {usr.active ? 'Active' : 'Blocked'}
                                                    </span>
                                                </td>
                                                <td>{usr.registrationDate ? new Date(usr.registrationDate).toLocaleDateString() : 'N/A'}</td>
                                                <td>
                                                    <div className="d-flex justify-content-end gap-2">
                                                        {/* Block/Unblock toggle */}
                                                        {usr.userTypeId?.userTypeName !== 'Admin' && (
                                                            <button 
                                                                className={`btn btn-action-text ${usr.active ? 'btn-warn' : 'btn-ok'}`}
                                                                title={usr.active ? 'Block User' : 'Unblock User'}
                                                                onClick={() => handleToggleUserStatus(usr.userId)}
                                                            >
                                                                <i className={`fa-solid ${usr.active ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                                                                {usr.active ? 'Block' : 'Unblock'}
                                                            </button>
                                                        )}
                                                        {/* Delete account */}
                                                        {usr.userTypeId?.userTypeName !== 'Admin' && (
                                                            <button 
                                                                className="btn btn-action-text btn-danger-action"
                                                                title="Delete User Account"
                                                                onClick={() => handleDeleteUser(usr.userId)}
                                                            >
                                                                <i className="fa-solid fa-trash-can"></i>
                                                                Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!loadingUsers && usersPage.totalPages > 1 && (
                            <div className="pagination-bar mt-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <span className="text-muted text-small">Page {usersCurrentPage + 1} of {usersPage.totalPages}</span>
                                <div className="btn-group">
                                    <button 
                                        className="btn btn-outline-cyan btn-sm" 
                                        disabled={usersCurrentPage === 0}
                                        onClick={() => fetchUsers(usersCurrentPage - 1)}
                                    >
                                        <i className="fa-solid fa-chevron-left me-1"></i>Previous
                                    </button>
                                    <button 
                                        className="btn btn-outline-cyan btn-sm" 
                                        disabled={usersCurrentPage + 1 >= usersPage.totalPages}
                                        onClick={() => fetchUsers(usersCurrentPage + 1)}
                                    >
                                        Next<i className="fa-solid fa-chevron-right ms-1"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 3: MODERATE JOBS */}
                {activeTab === 'jobs' && (
                    <div className="fade-in">
                        <h2 className="page-heading mb-4"><i className="fa-solid fa-briefcase me-2 text-cyan"></i>Job Listings Moderation</h2>

                        {/* Filters */}
                        <div className="filter-bar bg-glass p-3 rounded mb-4">
                            <div className="row g-3">
                                <div className="col-12 col-md-8">
                                    <div className="input-group search-group">
                                        <span className="input-group-text"><i className="fa-solid fa-magnifying-glass"></i></span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Search by job title, company..."
                                            value={jobsSearch}
                                            onChange={(e) => setJobsSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && fetchJobs(0)}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-4 d-flex gap-2">
                                    <button className="btn btn-cyan text-dark w-100" onClick={() => fetchJobs(0)}>
                                        Search
                                    </button>
                                    <button className="btn btn-outline-cyan w-100" onClick={() => { setJobsSearch(''); fetchJobs(0); }}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="table-responsive bg-glass rounded border border-secondary border-opacity-20 shadow-sm">
                            <table className="table custom-table mb-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Job Title</th>
                                        <th>Company</th>
                                        <th>Location</th>
                                        <th>Posted By</th>
                                        <th>Status</th>
                                        <th>Posted Date</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingJobs ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5">
                                                <div className="spinner-border text-cyan" role="status"></div>
                                            </td>
                                        </tr>
                                    ) : jobsPage.content.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4 text-muted">No jobs listed.</td>
                                        </tr>
                                    ) : (
                                        jobsPage.content.map((job) => (
                                            <tr key={job.jobPostId}>
                                                <td>#{job.jobPostId}</td>
                                                <td>
                                                    <span className="fw-semibold text-white">{job.jobTitle}</span>
                                                </td>
                                                <td>{job.jobCompanyId?.name || 'N/A'}</td>
                                                <td>{job.jobLocationId?.city}, {job.jobLocationId?.country}</td>
                                                <td>{job.postedById?.email || 'N/A'}</td>
                                                <td>
                                                    <span className={`badge-status ${job.isActive ? 'bg-active' : 'bg-inactive'}`}>
                                                        {job.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>{job.postedDate ? new Date(job.postedDate).toLocaleDateString() : 'N/A'}</td>
                                                <td>
                                                    <div className="d-flex justify-content-end gap-2">
                                                        {/* Toggle status */}
                                                        <button 
                                                            className={`btn btn-action-text ${job.isActive ? 'btn-warn' : 'btn-ok'}`}
                                                            title={job.isActive ? 'Deactivate Post' : 'Activate Post'}
                                                            onClick={() => handleToggleJobStatus(job.jobPostId)}
                                                        >
                                                            <i className={`fa-solid ${job.isActive ? 'fa-ban' : 'fa-check'}`}></i>
                                                            {job.isActive ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                        {/* Delete job */}
                                                        <button 
                                                            className="btn btn-action-text btn-danger-action"
                                                            title="Delete Job Post"
                                                            onClick={() => handleDeleteJob(job.jobPostId)}
                                                        >
                                                            <i className="fa-solid fa-trash-can"></i>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!loadingJobs && jobsPage.totalPages > 1 && (
                            <div className="pagination-bar mt-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <span className="text-muted text-small">Page {jobsCurrentPage + 1} of {jobsPage.totalPages}</span>
                                <div className="btn-group">
                                    <button 
                                        className="btn btn-outline-cyan btn-sm" 
                                        disabled={jobsCurrentPage === 0}
                                        onClick={() => fetchJobs(jobsCurrentPage - 1)}
                                    >
                                        <i className="fa-solid fa-chevron-left me-1"></i>Previous
                                    </button>
                                    <button 
                                        className="btn btn-outline-cyan btn-sm" 
                                        disabled={jobsCurrentPage + 1 >= jobsPage.totalPages}
                                        onClick={() => fetchJobs(jobsCurrentPage + 1)}
                                    >
                                        Next<i className="fa-solid fa-chevron-right ms-1"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 5: MANAGE APPLICATIONS */}
                {activeTab === 'applications' && (
                    <div className="fade-in">
                        <h2 className="page-heading mb-4"><i className="fa-solid fa-file-invoice me-2 text-cyan"></i>Job Applications Moderation</h2>

                        {/* Filters */}
                        <div className="filter-bar bg-glass p-3 rounded mb-4">
                            <div className="row g-3">
                                <div className="col-12 col-md-8">
                                    <div className="input-group search-group">
                                        <span className="input-group-text"><i className="fa-solid fa-magnifying-glass"></i></span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Search by candidate name, company, or job title..."
                                            value={applicationsSearch}
                                            onChange={(e) => setApplicationsSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && fetchApplications(0)}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-4 d-flex gap-2">
                                    <button className="btn btn-cyan text-dark w-100" onClick={() => fetchApplications(0)}>
                                        Search
                                    </button>
                                    <button className="btn btn-outline-cyan w-100" onClick={() => { setApplicationsSearch(''); fetchApplications(0); }}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* List */}
                        <div className="table-responsive bg-glass rounded border border-secondary border-opacity-20 shadow-sm">
                            <table className="table custom-table mb-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Candidate</th>
                                        <th>Job Title</th>
                                        <th>Company</th>
                                        <th>Apply Date</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingApplications ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <div className="spinner-border text-cyan" role="status"></div>
                                            </td>
                                        </tr>
                                    ) : applicationsPage.content.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4 text-muted">No applications listed.</td>
                                        </tr>
                                    ) : (
                                        applicationsPage.content.map((app) => (
                                            <tr key={app.id}>
                                                <td>#{app.id}</td>
                                                <td>
                                                    <span className="fw-semibold text-white">
                                                        {app.userId?.firstName || 'Unknown'} {app.userId?.lastName || 'Candidate'}
                                                    </span>
                                                    <br />
                                                    <small className="text-muted">{app.userId?.email || ''}</small>
                                                </td>
                                                <td>{app.job?.jobTitle || 'N/A'}</td>
                                                <td>{app.job?.jobCompanyId?.name || 'N/A'}</td>
                                                <td>
                                                    {app.applyDate ? new Date(app.applyDate).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td>
                                                    <span className={`badge bg-opacity-10 text-uppercase text-small px-2 py-1 ${
                                                        app.status === 'ACCEPTED' || app.status === 'HIRED'
                                                            ? 'bg-success text-success'
                                                            : app.status === 'REJECTED'
                                                            ? 'bg-danger text-danger'
                                                            : 'bg-warning text-warning'
                                                    }`}>
                                                        {app.status || 'APPLIED'}
                                                    </span>
                                                </td>
                                                <td className="text-end">
                                                    <div className="btn-action-group">
                                                        <button 
                                                            className="btn btn-action-text btn-danger-action"
                                                            title="Delete Application"
                                                            onClick={() => handleDeleteApplication(app.id)}
                                                        >
                                                            <i className="fa-solid fa-trash-can"></i>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!loadingApplications && applicationsPage.totalPages > 1 && (
                            <div className="pagination-bar mt-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <span className="text-muted text-small">Page {applicationsCurrentPage + 1} of {applicationsPage.totalPages}</span>
                                <div className="btn-group">
                                    <button 
                                        className="btn btn-outline-cyan btn-sm" 
                                        disabled={applicationsCurrentPage === 0}
                                        onClick={() => fetchApplications(applicationsCurrentPage - 1)}
                                    >
                                        <i className="fa-solid fa-chevron-left me-1"></i>Previous
                                    </button>
                                    <button 
                                        className="btn btn-outline-cyan btn-sm" 
                                        disabled={applicationsCurrentPage + 1 >= applicationsPage.totalPages}
                                        onClick={() => fetchApplications(applicationsCurrentPage + 1)}
                                    >
                                        Next<i className="fa-solid fa-chevron-right ms-1"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 4: SYSTEM MAINTENANCE */}
                {activeTab === 'maintenance' && (
                    <div className="fade-in">
                        <h2 className="page-heading mb-4"><i className="fa-solid fa-screwdriver-wrench me-2 text-cyan"></i>Database Maintenance</h2>

                        <div className="row g-4">
                            <div className="col-12 col-lg-7">
                                <div className="card-maint bg-glass p-4 rounded mb-4">
                                    <h5><i className="fa-solid fa-broom text-cyan me-2"></i>Job Postings Retention Cleanup</h5>
                                    <p className="text-muted text-small mt-2">
                                        Per compliance policies and database optimization guidelines:
                                    </p>
                                    <ul className="text-muted text-small ps-3">
                                        <li>Job posts are automatically <strong>deactivated</strong> after 60 days (soft-archived and removed from public search, but kept for analytics).</li>
                                        <li>Older job posts can be <strong>permanently deleted</strong> to optimize table sizing and clear indexed files from storage.</li>
                                    </ul>
                                    <div className="alert alert-warning border border-warning border-opacity-20 bg-warning bg-opacity-5 text-warning text-small my-4">
                                        <i className="fa-solid fa-circle-exclamation me-2"></i>
                                        <strong>Caution:</strong> Permanently deleting a job post will trigger a cascade delete, removing all candidate application logs and resume references associated with that post! This operation is irreversible.
                                    </div>

                                    <div className="cleanup-form d-flex align-items-end gap-3 flex-wrap">
                                        <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                                            <label className="form-label text-cyan fw-semibold text-small">Purge Jobs Older Than (Days):</label>
                                            <input 
                                                type="number" 
                                                className="form-control" 
                                                min="61" 
                                                value={cleanupDays}
                                                onChange={(e) => setCleanupDays(parseInt(e.target.value, 10))}
                                            />
                                        </div>
                                        <button 
                                            className="btn btn-danger-maint flex-grow-1 flex-md-grow-0"
                                            disabled={cleaning || isNaN(cleanupDays) || cleanupDays <= 60}
                                            onClick={handleRunCleanup}
                                        >
                                            {cleaning ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Cleaning...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-circle-minus me-2"></i>Run Database Maintenance
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-12 col-lg-5">
                                <div className="card-maint bg-glass p-4 rounded">
                                    <h5><i className="fa-solid fa-circle-info text-cyan me-2"></i>Portal System Info</h5>
                                    <div className="sys-info-item mt-4">
                                        <span className="info-label text-muted">Web App Environment</span>
                                        <span className="info-value text-white fw-semibold">Production Node v18+</span>
                                    </div>
                                    <div className="sys-info-item">
                                        <span className="info-label text-muted">Backend Services</span>
                                        <span className="info-value text-white fw-semibold">Spring Boot 3.4 (Java 21)</span>
                                    </div>
                                    <div className="sys-info-item">
                                        <span className="info-label text-muted">Primary Cache</span>
                                        <span className="info-value text-white fw-semibold">Redis Cache Active</span>
                                    </div>
                                    <div className="sys-info-item mb-0">
                                        <span className="info-label text-muted">Database Engine</span>
                                        <span className="info-value text-white fw-semibold">PostgreSQL 15+ (Supabase)</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminDashboardPage;
